// frontend/src/i18n.js
// frontend/src/i18n.js
import i18n from "i18next";

import {
  initReactI18next,
} from "react-i18next";

import HttpBackend from "i18next-http-backend";


const storedLanguage =
  localStorage.getItem(
    "app.lang"
  ) ||
  "en";


document.documentElement.setAttribute(
  "lang",
  storedLanguage
);


/*
 * ==========================================================================
 * LOADEvo namespaces
 * ==========================================================================
 *
 * Central manifest of every translation namespace used by the Dispatcher.
 *
 * These namespaces are preloaded globally after the essential application
 * namespaces have initialized.
 *
 * Result:
 *
 * route opens
 *      ↓
 * translation already exists in i18next memory
 *      ↓
 * no "tiles.foo.title" -> "Real title" visual replacement
 */

const APP_NAMESPACES = [
  "account_access",

  "assign_driver",
  "assign_trailer",
  "assign_truck",

  "billing_review",
  
  "carrier_details",

  "clients",

  "cmr_load_modal",
  "cmr_upload_modal",

  "company_profile",
  "company_settings",

  "contact_information",

  "create_carrier",
  "create_industry",
  "create_invoice",
  "create_load",
  "create_pendentload",
  "create_place",
  "create_purchase_orders",
  "create_trailer",
  "create_truck",
  "create_user_modal_page",

  "dashboard",

  "dispatcher_notifications",

  "driver_nickname_input",
  "driver_selector_modal",
  "driveroriginal",
  "drivers",

  "edit_carrier",
  "edit_driver",
  "edit_industry",
  "edit_invoice",
  "edit_load_bearers",
  "edit_load",
  "edit_normal_load",
  "edit_pendent_special_load",
  "edit_pendentload_bearer",
  "edit_pendentload",
  "edit_place",
  "edit_purchase_orders",
  "edit_special_load",
  "edit_superimposable_load",
  "edit_trailer",
  "edit_truck",

  "financial_information",

  "general_settings",

  "industry_details",

  "invoice_details",
  "invoices",

  "load_history_details",
  "load_history",
  "loads",

  "map",

  "notes_modal",

  "operation_details",

  "payment_terms",

  "pendent_loads",
  "pendent_notes_modal",

  "permissions_settings",

  "personnel",

  "personal_info",

  "place_details",
  "places",

  "plans_subscription",

  "profile_settings",

  "purchase_orders",

  "settings_invoices_statements",
  "settings_nav",

  "side_bar",

  "status_selector_modal",

  "strategic_point_modal",

  "super_imposable_load",

  "tax_information",

  "trailer_details",
  "trailer_inventory_modal",
  "trailer_selector_modal",

  "transport_session_locations",
  "transport_session_map_modal",

  "truck_details",
  "truck_inventory_modal",
  "truck_selector_modal",
  "trucks",

  "user_details_modal",
  "users_settings",
];

/*
 * ==========================================================================
 * Initial route namespaces
 * ==========================================================================
 *
 * On a hard refresh React must not mount before the namespace used by the
 * current route is available.
 *
 * We only block the first render for the small critical set.
 * The complete catalogue continues warming in the background afterwards.
 */

const normalizeRouteToken =
  (
    value
  ) =>
    String(
      value ||
        ""
    )
      .trim()
      .toLowerCase()
      .replace(
        /-/g,
        "_"
      );


export const getInitialRouteNamespaces =
  (
    pathname =
      window.location
        .pathname
  ) => {
    const tokens =
      String(
        pathname ||
          "/"
      )
        .split("/")
        .filter(
          Boolean
        )
        .map(
          normalizeRouteToken
        );


    const namespaces =
      new Set([
        "common",
        "side_bar",
      ]);


    const first =
      tokens[0] ||
      "";

    const second =
      tokens[1] ||
      "";


    /*
     * Most LOADEvo routes map directly:
     *
     * /personnel -> personnel
     * /clients   -> clients
     * /places    -> places
     * /loads     -> loads
     * /invoices  -> invoices
     */
    if (
      first &&
      APP_NAMESPACES.includes(
        first
      )
    ) {
      namespaces.add(
        first
      );
    }


    /*
     * Also support route names containing several URL segments.
     */
    if (
      tokens.length >
      1
    ) {
      const joined =
        tokens.join(
          "_"
        );

      if (
        APP_NAMESPACES.includes(
          joined
        )
      ) {
        namespaces.add(
          joined
        );
      }
    }


    /*
     * Settings routes use names such as:
     *
     * /settings/profile
     *   -> profile_settings
     *
     * /settings/permissions
     *   -> permissions_settings
     *
     * /settings/invoices-statements
     *   -> settings_invoices_statements
     */
    if (
      first ===
      "settings"
    ) {
      namespaces.add(
        "settings_nav"
      );

      if (second) {
        const suffixCandidate =
          `${second}_settings`;

        const prefixCandidate =
          `settings_${second}`;


        if (
          APP_NAMESPACES.includes(
            suffixCandidate
          )
        ) {
          namespaces.add(
            suffixCandidate
          );
        }


        if (
          APP_NAMESPACES.includes(
            prefixCandidate
          )
        ) {
          namespaces.add(
            prefixCandidate
          );
        }
      }
    }


    return Array.from(
      namespaces
    );
  };


const INITIAL_NAMESPACES =
  getInitialRouteNamespaces();


let initialI18nLoadComplete =
  false;

/*
 * ==========================================================================
 * Preload state
 * ==========================================================================
 *
 * Keep one preload promise per language.
 *
 * This prevents:
 *
 * - duplicate namespace downloads;
 * - StrictMode causing unnecessary work;
 * - several components asking for the same language preload simultaneously.
 */

const preloadPromisesByLanguage =
  new Map();


export const preloadApplicationNamespaces =
  () => {
    const language =
      String(
        i18n.resolvedLanguage ||
        i18n.language ||
        storedLanguage ||
        "en"
      )
        .split("-")[0]
        .toLowerCase();


    const existingPromise =
      preloadPromisesByLanguage.get(
        language
      );


    if (
      existingPromise
    ) {
      return existingPromise;
    }


    const promise =
      i18n
        .loadNamespaces(
          APP_NAMESPACES
        )
        .catch(
          (
            error
          ) => {
            /*
             * Allow another attempt later if the preload genuinely failed.
             */
            preloadPromisesByLanguage.delete(
              language
            );

            console.warn(
              `[i18n] namespace preload failed for "${language}":`,
              error
            );

            throw error;
          }
        );


    preloadPromisesByLanguage.set(
      language,
      promise
    );


    return promise;
  };


/*
 * ==========================================================================
 * i18next initialization
 * ==========================================================================
 */

export const i18nReady =
  i18n
    .use(
      HttpBackend
    )
    .use(
      initReactI18next
    )
    .init({
      lng:
        storedLanguage,

      fallbackLng:
        "en",

      /*
       * Critical initial load.
       *
       * React will wait for these namespaces before its
       * very first render.
       *
       * Example on /personnel:
       *
       * common
       * side_bar
       * personnel
       */
      ns:
        INITIAL_NAMESPACES,

      defaultNS:
        "common",

      backend: {
        loadPath:
          "/locales/{{lng}}/{{ns}}.json",
      },

      load:
        "languageOnly",

      interpolation: {
        escapeValue:
          false,
      },

      react: {
        useSuspense:
          false,
      },

      debug:
        false,
    })
    .then(
      () => {
        initialI18nLoadComplete =
          true;

        /*
         * The first screen is now safe to render.
         *
         * Warm the rest of Dispatcher in the background
         * so subsequent navigation remains instant.
         */
        preloadApplicationNamespaces()
          .catch(
            () => {
              // Already logged above.
            }
          );

        return i18n;
      }
    )
    .catch(
      (
        error
      ) => {
        /*
         * Never leave the entire application permanently
         * blank if translations genuinely fail.
         */
        console.error(
          "[i18n] initialization failed:",
          error
        );

        initialI18nLoadComplete =
          true;

        return i18n;
      }
    );

/*
 * ==========================================================================
 * Language changes
 * ==========================================================================
 *
 * If the user changes:
 *
 * EN -> PT
 * PT -> ES
 * ES -> FR
 *
 * immediately warm the whole catalogue for the new language too.
 */

i18n.on(
  "languageChanged",
  (
    language
  ) => {
    const normalizedLanguage =
      String(
        language ||
        ""
      )
        .split("-")[0]
        .toLowerCase();


    if (
      normalizedLanguage
    ) {
      document.documentElement.setAttribute(
        "lang",
        normalizedLanguage
      );

      try {
        localStorage.setItem(
          "app.lang",
          normalizedLanguage
        );
      } catch {
        // ignore storage failures
      }
    }


    /*
     * i18next can emit languageChanged during its own
     * initialization.
     *
     * Do not start the complete catalogue preload before
     * the critical first-screen namespaces have finished.
     */
    if (
      !initialI18nLoadComplete
    ) {
      return;
    }


    preloadApplicationNamespaces()
      .catch(
        () => {
          // Already logged above.
        }
      );
  }
);


export default i18n;